import React from 'react';
import { MapPin, Calendar, Users, Target } from 'lucide-react';
import { companyInfo } from '../data/mock';

const AboutSection = () => {
  const highlights = [
    {
      icon: Calendar,
      label: 'Established',
      value: companyInfo.established,
      color: 'bg-sky-100 text-sky-600 group-hover:bg-sky-500'
    },
    {
      icon: MapPin,
      label: 'Based in',
      value: 'Maldives',
      color: 'bg-yellow-100 text-yellow-600 group-hover:bg-yellow-400'
    },
    {
      icon: Users,
      label: 'Focus',
      value: 'Resort Development',
      color: 'bg-green-100 text-green-600 group-hover:bg-green-500'
    },
    {
      icon: Target,
      label: 'Approach',
      value: 'End-to-End Solutions',
      color: 'bg-orange-100 text-orange-600 group-hover:bg-orange-500'
    }
  ];

  return (
    <section id="about" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Text Content */}
          <div className="space-y-8">
            <div>
              <span className="inline-block text-sky-500 font-semibold text-sm uppercase tracking-wider mb-3">
                Who We Are
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 leading-tight">
                About Touricon Consultancy
              </h2>
            </div>

            <p className="text-slate-600 text-lg leading-relaxed">
              {companyInfo.about}
            </p>

            {/* Highlights Grid */}
            <div className="grid grid-cols-2 gap-4 pt-4">
              {highlights.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 p-4 bg-slate-50 rounded-xl hover:bg-sky-50 transition-colors group"
                >
                  <div className={`w-10 h-10 rounded-lg ${item.color.split(' ')[0]} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <item.icon className={`w-5 h-5 ${item.color.split(' ')[1]}`} />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">
                      {item.label}
                    </p>
                    <p className="font-semibold text-slate-800">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Visual Element */}
          <div className="relative">
            <div className="relative bg-gradient-to-br from-slate-100 to-sky-50 rounded-3xl p-8 lg:p-12">
              {/* Decorative circles like logo */}
              <div className="absolute top-4 right-4 w-16 h-16 border-4 border-sky-300 rounded-full opacity-60"></div>
              <div className="absolute bottom-8 left-4 w-12 h-12 border-4 border-yellow-300 rounded-full opacity-60"></div>
              <div className="absolute top-1/2 right-8 w-10 h-10 border-4 border-green-300 rounded-full opacity-60"></div>
              <div className="absolute bottom-1/3 left-8 w-8 h-8 border-4 border-orange-300 rounded-full opacity-60"></div>
              
              <div className="relative z-10 space-y-6">
                <img 
                  src={companyInfo.logo} 
                  alt="Touricon Consultancy" 
                  className="h-20 w-auto object-contain"
                />
                
                <h3 className="text-2xl font-bold text-slate-800">
                  Your Trusted Partner in Maldives Resort Investing & Development
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-slate-600">Deep understanding of local regulations</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-yellow-400 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-slate-600">Extensive industry expertise</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-slate-600">Sustainable & compliant projects</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
